import {
  a,
  div,
  li,
  span,
  text,
  type ChildNode,
  type ElProps,
  type Mountable,
} from '@llui/dom';

import { entityIcon, type EntityIconRoot } from '../atoms/entity-icon.js';

/**
 * `entityCard(...)` — the compact entity preview (per `30-design-system.md §8.2`).
 *
 * This is the single reusable "entity row": an icon tile + a default sans
 * name + an optional JetBrains-Mono metadata line + an optional trailing
 * slot. It is the row for **entity lists, command-palette results,
 * search results, ref-popouts**, and is designed to also carry **future
 * member / pack / generic result rows**:
 *
 *   - The icon is selected by `root` (an `EntityRoot`). When a future
 *     row is *not* an entity (a pack, an account member, a Map), pass
 *     `icon` directly to override the root-derived `entityIcon` — the
 *     tile chrome stays identical, only the glyph changes.
 *   - `meta` is the mono descriptor line ("Location · city · capital",
 *     "Faction · 23 members", "pack: core-fantasy · v3"). Optional.
 *   - `trailing` is the right-hand slot — a palette score, a lane tag,
 *     a member-role chip, a kebab. Optional.
 *
 * The treatment matches `mocks/command-palette.html` `.result`: a
 * borderless `grid-template-columns: <tile> 1fr auto` row separated by
 * hover background (NOT a bordered box). Layout + colours live in
 * `entity-card.css` (exported via the package `exports` map) and read
 * the design-system tokens (`--surface-*`, `--text-*`, `--accent*`).
 *
 * **Element**: defaults to a `<div>`. Pass `as: 'li'` for listbox/list
 * rows (the palette/list use `role="option"`/`<li>`). When `href` is
 * set, the name becomes an `<a>` (clickable); otherwise the whole card
 * is inert chrome and the caller wires interaction on the container via
 * `attrs`.
 *
 * **A11y**: the icon is decorative (`aria-hidden`) — the root identity
 * is always present in the visible text (the name and/or the `meta`
 * line), so colour/shape is never the only signal (§10.8). `active`
 * toggles the `is-current` highlight (the palette's keyboard cursor);
 * the caller still owns `aria-selected`.
 *
 * Slots accept a `ChildNode` (a bare string, or a reactive `text(...)`
 * node) so the card composes inside `each` rows where name/meta are
 * `item.at(...)` signals.
 */

export interface EntityCardOptions {
  /** EntityRoot — selects the Lucide icon + per-root tile tint. */
  root?: EntityIconRoot | string;
  /** Override the tile glyph (for non-entity rows: packs, members, …). */
  icon?: Mountable;
  /** Primary label (default sans). String or a reactive `text(...)` node. */
  name: ChildNode;
  /** Mono descriptor line. Omit to render name-only. */
  meta?: ChildNode;
  /** Optional rich snippet line (italic default sans) — e.g. an FTS excerpt. */
  snippet?: ChildNode;
  /** Right-hand slot — score chip, lane tag, role chip, kebab, … */
  trailing?: ChildNode;
  /**
   * When set, the name renders as an anchor with this href. Accepts a
   * static string or a reactive attr signal (`item.map(...)`).
   */
  href?: ElProps[string];
  /** Click handler on the name anchor (only used with `href`). */
  onNameClick?: (ev: MouseEvent) => void;
  /** Extra attributes merged onto the name anchor/span (e.g. a testid). */
  nameAttrs?: ElProps;
  /** Highlight as the active/cursor row (`data-active`). */
  active?: boolean;
  /** Container element — `'div'` (default) or `'li'` for list rows. */
  as?: 'div' | 'li';
  /** Extra container class. */
  className?: string;
  /** Forwarded `data-testid` on the container. */
  testid?: string;
  /** Forwarded `data-root` on the container (defaults to `root`). */
  dataRoot?: string;
  /** Arbitrary extra attributes merged onto the container (role, etc.). */
  attrs?: ElProps;
}

export function entityCard(opts: EntityCardOptions): Mountable {
  const dataRoot = opts.dataRoot ?? (typeof opts.root === 'string' ? opts.root : undefined);

  const tile = span(
    {
      class: 'dl-entity-card-icon',
      'data-root': dataRoot,
      'aria-hidden': 'true',
    },
    [opts.icon ?? entityIcon(opts.root ?? 'lore')],
  );

  const nameNode: Mountable =
    opts.href !== undefined
      ? a(
          {
            ...(opts.nameAttrs ?? {}),
            class: 'dl-entity-card-name',
            href: opts.href,
            ...(opts.onNameClick ? { onClick: opts.onNameClick } : {}),
          },
          [renderSlot(opts.name)],
        )
      : span(
          { ...(opts.nameAttrs ?? {}), class: 'dl-entity-card-name' },
          [renderSlot(opts.name)],
        );

  const bodyChildren: Mountable[] = [nameNode];
  if (opts.snippet !== undefined) {
    bodyChildren.push(span({ class: 'dl-entity-card-snippet' }, [renderSlot(opts.snippet)]));
  }
  if (opts.meta !== undefined) {
    bodyChildren.push(span({ class: 'dl-entity-card-meta' }, [renderSlot(opts.meta)]));
  }
  const body = div({ class: 'dl-entity-card-body' }, bodyChildren);

  const children: Mountable[] = [tile, body];
  if (opts.trailing !== undefined) {
    children.push(span({ class: 'dl-entity-card-trail' }, [renderSlot(opts.trailing)]));
  }

  const attrs: ElProps = {
    ...(opts.attrs ?? {}),
    class: opts.className ? `dl-entity-card ${opts.className}` : 'dl-entity-card',
    'data-testid': opts.testid,
    'data-root': dataRoot,
    'data-active': opts.active ? 'true' : undefined,
  };

  return opts.as === 'li' ? li(attrs, children) : div(attrs, children);
}

/** Wrap a bare string/number in `text()`; pass through Mountable nodes. */
function renderSlot(value: ChildNode): Mountable {
  return typeof value === 'string' || typeof value === 'number'
    ? text(String(value))
    : value;
}
